import React, { useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { errorMessage, type AdminApi, type AdminDeck } from '../api';
import type { Route } from '../router';

function sortDecks(decks: AdminDeck[]): AdminDeck[] {
  return decks.slice().sort((a, b) => {
    const ga = a.group_name ?? '';
    const gb = b.group_name ?? '';
    if (ga !== gb) return ga.localeCompare(gb);
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.title.localeCompare(b.title);
  });
}

interface CreateValues {
  title: string;
  group_name?: string;
  description?: string;
}

export const DeckListScreen: React.FC<{
  api: AdminApi;
  navigate: (route: Route) => void;
}> = ({ api, navigate }) => {
  const { message } = AntApp.useApp();
  const [decks, setDecks] = useState<AdminDeck[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [form] = Form.useForm<CreateValues>();

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .listDecks()
      .then((list) => {
        if (cancelled) return;
        setDecks(sortDecks(list));
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const create = async (values: CreateValues) => {
    setCreateBusy(true);
    try {
      const deck = await api.createDeck({
        title: values.title.trim(),
        description: values.description?.trim() || undefined,
        group_name: values.group_name?.trim() || undefined,
      });
      setCreating(false);
      form.resetFields();
      navigate({ name: 'deck', id: deck.id });
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setCreateBusy(false);
    }
  };

  const remove = async (deck: AdminDeck) => {
    try {
      await api.deleteDeck(deck.id);
      setDecks((prev) => (prev ? prev.filter((d) => d.id !== deck.id) : prev));
      message.success(`Deleted "${deck.title}"`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const columns = [
    {
      title: 'Deck',
      dataIndex: 'title',
      key: 'title',
      render: (_: unknown, d: AdminDeck) => (
        <div>
          <Typography.Link onClick={() => navigate({ name: 'deck', id: d.id })} strong>
            {d.title}
          </Typography.Link>
          {d.description && (
            <div>
              <Typography.Text
                type="secondary"
                ellipsis
                style={{ maxWidth: 360, display: 'block' }}
              >
                {d.description}
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'group_name',
      key: 'group',
      render: (g: string | null) => g || <Typography.Text type="secondary">None</Typography.Text>,
    },
    { title: 'Words', dataIndex: 'word_count', key: 'words', align: 'right' as const },
    {
      title: 'Cards ready',
      key: 'ready',
      render: (_: unknown, d: AdminDeck) => (
        <div style={{ minWidth: 120 }}>
          <div>{`${d.ready_count} of ${d.word_count}`}</div>
          <Progress
            percent={d.word_count ? Math.round((d.ready_count / d.word_count) * 100) : 0}
            showInfo={false}
            size="small"
            status={d.word_count > 0 && d.ready_count === d.word_count ? 'success' : 'active'}
          />
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: AdminDeck['status']) =>
        s === 'published' ? <Tag color="green">Published</Tag> : <Tag>Draft</Tag>,
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_: unknown, d: AdminDeck) => (
        <Space>
          <Button size="small" onClick={() => navigate({ name: 'deck', id: d.id })}>
            Open
          </Button>
          <Popconfirm
            title={`Delete "${d.title}"?`}
            description={
              d.status === 'published'
                ? 'This deck is published: learners lose it. Their cards and progress stay.'
                : 'The deck and its word list are removed. This cannot be undone.'
            }
            okText="Delete deck"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => remove(d)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete ${d.title}`}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          Public decks
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          New deck
        </Button>
      </div>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the decks"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<AdminDeck>
        rowKey="id"
        loading={decks === null && !loadError}
        dataSource={decks ?? []}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: <Empty description="No public decks yet. Create the first one." />,
        }}
      />

      <Modal
        title="New public deck"
        open={creating}
        okText="Create deck"
        confirmLoading={createBusy}
        onOk={() => form.submit()}
        onCancel={() => setCreating(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={create} requiredMark={false}>
          <Form.Item
            name="title"
            label="Name"
            rules={[{ required: true, whitespace: true, message: 'Give the deck a name' }]}
          >
            <Input autoFocus placeholder="At the doctor" maxLength={120} />
          </Form.Item>
          <Form.Item name="group_name" label="Group" extra="The heading the deck is listed under.">
            <Input placeholder="Life in Germany" maxLength={80} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
